import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProAuth } from '@/lib/ProAuthContext';
import { Loader2 } from 'lucide-react';

/**
 * AuthCallback Component
 * Handles the redirect from Google OAuth (Emergent Auth)
 * Extracts session_id from URL fragment and exchanges it for user data
 */
const ProAuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { exchangeSession } = useProAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment (hash)
        // Format: /pro/callback#session_id=xxx
        const hash = location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session_id in URL');
          navigate('/pro/login?error=no_session');
          return;
        }

        // Exchange session_id for user data
        const userData = await exchangeSession(sessionId);
        
        // Clear the URL fragment and redirect to dashboard
        // Pass user data to avoid another auth check
        navigate('/pro/dashboard', { 
          replace: true,
          state: { user: userData }
        });
        
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/pro/login?error=auth_failed');
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
        <p className="text-gray-600">Signing you in...</p>
      </div>
    </div>
  );
};

export default ProAuthCallback;
